import { apiGet, apiPost, apiPatch, apiDelete, ApiClientError, API_BASE_URL, getBaseAuthHeaders } from './client.js';

// Types
export interface ProjectLabel {
  id: number;
  name: string | null;
}

export interface Project {
  id: number;
  name: string;
  fullName: string | null;
  state: string;
  isDefault: boolean;
  mandatoryTasks: boolean;
  defaultRate: number | null;
  customer: {
    id: number;
    name: string;
  } | null;
  /**
   * MKS-536 — the two project properties the locked-month move rule reads. Both
   * come from `/time/projects`; `customerId` was already in the payload, and
   * `billable === false` is what the product calls "Internal".
   */
  customerId?: number | null;
  billable?: boolean;
  labels?: ProjectLabel[];
}

export interface Entry {
  id: number;
  projectId: number;
  date: string;
  hours: number;
  description: string | null;
  task: string | null;
  state: string;
  rejectionReason: string | null;
  userCosts: number | null;
  assignmentCosts: number | null;
  extraCosts: number | null;
  totalCosts: number | null;
  investmentRate: number | null;
  project: {
    id: number;
    name: string;
    fullName: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DailyEntriesResponse {
  data: {
    date: string;
    entries: Entry[];
    totalHours: number;
  };
}

export interface WeeklyEntriesResponse {
  data: {
    weekStart: string;
    weekEnd: string;
    entries: Entry[];
    dailyTotals: Record<string, number>;
    weekTotal: number;
  };
}

export interface TimesheetResponse {
  data: {
    id: number;
    state: string;
    period: {
      id: number;
      name: string;
      from: string;
      to: string;
    };
    rows: Array<{
      id: number;
      projectId: number;
      position: number | null;
      project: {
        id: number;
        name: string;
        fullName: string | null;
      };
    }>;
    totalHours: number;
    entries: Entry[];
  };
}

export interface PeriodResponse {
  data: {
    id: number;
    name: string;
    from: string;
    to: string;
  };
}

export interface ProjectsResponse {
  data: Project[];
}

export interface CreateEntryInput {
  projectId: number;
  date: string;
  hours: number;
  description?: string;
  task?: string;
}

export interface UpdateEntryInput {
  hours?: number;
  description?: string;
  task?: string;
  projectId?: number;
  date?: string;
}

// API Functions

export async function getDailyEntries(date?: string): Promise<DailyEntriesResponse> {
  const params = date ? `?date=${date}` : '';
  return apiGet<DailyEntriesResponse>(`/time/entries/daily${params}`);
}

export async function getWeeklyEntries(weekStart?: string): Promise<WeeklyEntriesResponse> {
  const params = weekStart ? `?week=${weekStart}` : '';
  return apiGet<WeeklyEntriesResponse>(`/time/entries/weekly${params}`);
}

export async function getDateRangeEntries(
  startDate: string,
  endDate: string
): Promise<{ data: { entries: Entry[] } }> {
  return apiGet(`/time/entries/range?startDate=${startDate}&endDate=${endDate}`);
}

export async function createEntry(data: CreateEntryInput): Promise<{ data: Entry }> {
  return apiPost('/time/entries', data);
}

export async function updateEntry(
  id: number,
  data: UpdateEntryInput
): Promise<{ data: Entry }> {
  return apiPatch(`/time/entries/${id}`, data);
}

export async function deleteEntry(id: number): Promise<{ data: { success: boolean } }> {
  return apiDelete(`/time/entries/${id}`);
}

export async function getCurrentTimesheet(): Promise<TimesheetResponse> {
  return apiGet('/time/timesheet/current');
}

export async function getTimesheetHistory(
  limit?: number
): Promise<{
  data: Array<{
    id: number;
    state: string;
    period: { name: string; from: string; to: string };
    totalHours: number;
    rejectionReason: string | null;
    rejectedAt: string | null;
    rejectedById: number | null;
  }>;
}> {
  const params = limit ? `?limit=${limit}` : '';
  return apiGet(`/time/timesheets${params}`);
}

export async function submitTimesheet(id: number): Promise<TimesheetResponse> {
  return apiPost(`/time/timesheets/${id}/submit`);
}

export async function getCurrentPeriod(): Promise<PeriodResponse> {
  return apiGet('/time/periods/current');
}

export async function getProjects(search?: string, asOf?: string): Promise<ProjectsResponse> {
  const qs = new URLSearchParams();
  if (search) qs.set('search', search);
  // asOf (YYYY-MM-DD) = viewed month start; lets a past month surface projects
  // whose assignment was active then even if it has since ended.
  if (asOf) qs.set('from', asOf);
  const query = qs.toString();
  return apiGet(`/time/projects${query ? `?${query}` : ''}`);
}

export async function getAllProjects(): Promise<ProjectsResponse> {
  return apiGet('/time/projects?all=true');
}

export interface ProjectTask {
  name: string;
  code: string;
  budget: number | null;
  budgetSpent: number | null;
  hardLimit: boolean;
  plannedHours: number | null;
  actualHours: number | null;
}

export async function getProjectTasks(projectId: number): Promise<{ data: ProjectTask[] }> {
  return apiGet(`/time/projects/${projectId}/tasks`);
}

export async function addTimesheetRow(
  timesheetId: number,
  projectId: number
): Promise<{ data: { success: boolean } }> {
  return apiPost(`/time/timesheets/${timesheetId}/rows`, { projectId });
}

export async function removeTimesheetRow(
  timesheetId: number,
  projectId: number
): Promise<{ data: { success: boolean } }> {
  return apiDelete(`/time/timesheets/${timesheetId}/rows/${projectId}`);
}

// Manager Workflow Types
export type TimesheetStatus = 'open' | 'submitted' | 'approved' | 'rejected';

export interface TimesheetSummary {
  id: number;
  state: TimesheetStatus;
  totalHours: number;
  submittedAt: string | null;
  /** Phase 28 D2 — populated only when state === 'rejected'. */
  rejectionReason: string | null;
  rejectedAt: string | null;
  rejectedById: number | null;
  period: {
    id: number;
    name: string;
    from: string;
    to: string;
  };
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  // Per-project manager fields
  managedProjectIds?: number[];
  approvableHours?: number; // Hours in 'submitted' state (actionable)
  pendingHours?: number; // Hours NOT in 'approved' state (open + submitted)
}

export interface ManagerContext {
  managedProjectIds: number[] | null; // null means all projects (admin)
  ownManagedProjectIds?: number[]; // Projects the current user manages
  canApprove: boolean;
  approvableEntryCount: number;
}

export interface TimesheetDetails extends TimesheetSummary {
  rows: Array<{
    id: number;
    projectId: number;
    position: number | null;
    project: {
      id: number;
      name: string;
      fullName: string | null;
    };
  }>;
  entries: Entry[];
  managerContext?: ManagerContext | null;
}

export interface StateChange {
  id: number;
  subjectType: string;
  subjectId: number;
  fromState: string | null;
  toState: string;
  reason: string | null;
  createdAt: string;
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface PendingTimesheetsResponse {
  data: TimesheetSummary[];
}

export interface TimesheetAggregateStats {
  totalHours: number;
  hoursByState: {
    approved: number;
    submitted: number;
    open: number;
    rejected: number;
  };
  countByState: {
    approved: number;
    submitted: number;
    open: number;
    rejected: number;
  };
}

export interface AllTimesheetsResponse {
  data: TimesheetSummary[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  aggregate: TimesheetAggregateStats | null;
}

export interface TimesheetDetailsResponse {
  data: TimesheetDetails;
}

export interface TimesheetHistoryResponse {
  data: StateChange[];
}

// Manager Workflow API Functions

export async function getPendingTimesheets(
  // `'all'` is an admin-only sentinel: the /pending endpoint returns every state
  // for the global (admin) scope. For a manager it is ignored (queue stays
  // submitted/rejected) — see getPendingTimesheets in timesheetService.ts.
  state?: TimesheetStatus | 'all',
  periodId?: number,
  userId?: number,
  scope?: 'all'
): Promise<PendingTimesheetsResponse> {
  const params = new URLSearchParams();
  if (state) params.append('state', state);
  if (periodId) params.append('periodId', String(periodId));
  if (userId) params.append('userId', String(userId));
  if (scope) params.append('scope', scope);
  const queryString = params.toString();
  return apiGet(`/time/timesheets/pending${queryString ? `?${queryString}` : ''}`);
}

// Admin endpoints for full timesheet approval
export async function getAdminPendingTimesheets(): Promise<PendingTimesheetsResponse> {
  return apiGet('/time/admin/timesheets/pending');
}

export async function adminApproveTimesheet(
  id: number,
  entryIds?: number[]
): Promise<TimesheetDetailsResponse> {
  return apiPost(`/time/admin/timesheets/${id}/approve`, entryIds ? { entryIds } : undefined);
}

export async function adminRejectTimesheet(
  id: number,
  reason?: string
): Promise<TimesheetDetailsResponse> {
  return apiPost(`/time/admin/timesheets/${id}/reject`, { reason });
}

export async function adminReopenTimesheet(id: number): Promise<TimesheetDetailsResponse> {
  return apiPost(`/time/admin/timesheets/${id}/reopen`);
}

export async function adminSubmitTimesheet(id: number): Promise<TimesheetDetailsResponse> {
  return apiPost(`/time/admin/timesheets/${id}/submit`);
}

// Bulk operations
export interface BulkOperationResponse {
  data: {
    processed: number;
    failed: number[];
  };
}

export async function bulkAdminSubmitTimesheets(ids: number[]): Promise<BulkOperationResponse> {
  return apiPost('/time/admin/timesheets/bulk/submit', { ids });
}

export async function bulkAdminApproveTimesheets(ids: number[]): Promise<BulkOperationResponse> {
  return apiPost('/time/admin/timesheets/bulk/approve', { ids });
}

export async function bulkAdminRejectTimesheets(ids: number[], reason?: string): Promise<BulkOperationResponse> {
  return apiPost('/time/admin/timesheets/bulk/reject', { ids, reason });
}

export async function adminDeleteTimesheet(id: number): Promise<{ data: { success: boolean } }> {
  return apiDelete(`/time/admin/timesheets/${id}`);
}

export async function bulkAdminDeleteTimesheets(ids: number[]): Promise<BulkOperationResponse> {
  return apiPost('/time/admin/timesheets/bulk/delete', { ids });
}

export interface AllTimesheetsExtraParams {
  // Date range for quarter/year granularity (YYYY-MM-DD). Ignored if periodId set.
  fromDate?: string;
  toDate?: string;
  // Server-side sort on DB-backed columns only.
  sortBy?: 'employee' | 'period' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export async function getAllTimesheets(
  page?: number,
  state?: string,
  periodId?: number,
  userId?: number,
  projectId?: number,
  pageSize?: number,
  includeAggregate?: boolean,
  extra?: AllTimesheetsExtraParams
): Promise<AllTimesheetsResponse> {
  const params = new URLSearchParams();
  if (page) params.append('page', String(page));
  if (state) params.append('state', state);
  if (periodId) params.append('periodId', String(periodId));
  if (userId) params.append('userId', String(userId));
  if (projectId) params.append('projectId', String(projectId));
  if (pageSize) params.append('pageSize', String(pageSize));
  if (includeAggregate) params.append('includeAggregate', 'true');
  if (extra?.fromDate) params.append('fromDate', extra.fromDate);
  if (extra?.toDate) params.append('toDate', extra.toDate);
  if (extra?.sortBy) params.append('sortBy', extra.sortBy);
  if (extra?.sortOrder) params.append('sortOrder', extra.sortOrder);
  const queryString = params.toString();
  return apiGet(`/time/timesheets/all${queryString ? `?${queryString}` : ''}`);
}

export async function getTimesheetDetails(id: number): Promise<TimesheetDetailsResponse> {
  return apiGet(`/time/timesheets/${id}`);
}

export async function approveTimesheet(
  id: number,
  entryIds?: number[],
  managerOnly?: boolean
): Promise<TimesheetDetailsResponse> {
  return apiPost(`/time/timesheets/${id}/approve`, {
    ...(entryIds ? { entryIds } : {}),
    ...(managerOnly ? { managerOnly: true } : {}),
  });
}

export async function rejectTimesheet(
  id: number,
  reason?: string,
  entryIds?: number[],
  managerOnly?: boolean
): Promise<TimesheetDetailsResponse> {
  return apiPost(`/time/timesheets/${id}/reject`, {
    reason,
    ...(entryIds ? { entryIds } : {}),
    ...(managerOnly ? { managerOnly: true } : {}),
  });
}

export async function getTimesheetStateHistory(id: number): Promise<TimesheetHistoryResponse> {
  return apiGet(`/time/timesheets/${id}/history`);
}

// Holiday API

export interface Holiday {
  date: string; // YYYY-MM-DD
  name: string;
}

export interface HolidaysResponse {
  data: Holiday[];
}

export async function getHolidays(year: number): Promise<HolidaysResponse> {
  return apiGet(`/holidays?year=${year}`);
}

// Import entries from Tempo XLS

export interface ImportProblems {
  userNotFound: string[];
  entryNotEditable: string[];
}

export interface ImportResult {
  imported: number;
  totalRows: number;
  rowsSkipped: number;
  aggregatedEntries: number;
  zeroHoursSkipped: number;
  problems: ImportProblems;
  dryRun: boolean;
}

export interface ImportEntriesResponse {
  data: ImportResult;
}

export async function importEntries(projectId: number, file: File, dryRun: boolean = false): Promise<ImportEntriesResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('projectId', String(projectId));
  if (dryRun) formData.append('dryRun', 'true');

  const response = await fetch(`${API_BASE_URL}/time/timesheets/import`, {
    method: 'POST',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      error: 'Unknown Error',
      message: response.statusText,
    }));
    throw new ApiClientError(error);
  }

  return response.json();
}
